import { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { SwitchTransition, Transition } from 'react-transition-group'

import UIParticles from '@/components/Particles/UIParticles'
import UIIcon, { UIIconType } from '@/components/UI/UIIcon'
import { useGSAP } from '@/composables/useGSAP'
import { useRunnerState } from '@/composables/useRunnerState'
import { UIAudio } from '@/config/AudioConfig'
import AudioEngine from '@/webgl/audio/AudioEngine'

const backgrounds = ['background1', 'background3', 'background2']
const colors = [
  'text-mid-blue', // 'text-light-blue',
  'text-mid-blue', // 'text-coral',
  'text-mid-blue',
]
const excludedColors = [['light-blue'], ['coral', 'peach'], ['mid-blue']]

export default function RunnerUICountdown() {
  const { context } = useRunnerState()

  const { gsap } = useGSAP()
  const [time, setTime] = useState(3)
  const nodeRef = useRef<HTMLDivElement>(null)
  const backgroundRef = useRef<SVGSVGElement>(null)
  const textRef = useRef<HTMLDivElement>(null)

  const segment = context.game.currentSegment
  const isVisible = segment === 'countdown'

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      if (isVisible) {
        const proxy = {
          value: 4,
        }
        gsap
          .timeline({
            // repeat: -1,
            onUpdate: () => {
              setTime(Math.floor(proxy.value))
            },
          })
          .fromTo(
            proxy,
            { value: 4 },
            {
              value: 0,
              duration: 3,
              ease: 'none',
            }
          )
      }
    })

    return () => ctx.revert()
  }, [gsap, setTime, isVisible])

  const onEnter = useCallback(() => {
    const ctx = gsap.context(() => {
      if (nodeRef.current && backgroundRef.current && textRef.current) {
        AudioEngine.play(UIAudio.CountdownStep)

        gsap
          .timeline()
          .fromTo(
            backgroundRef.current,
            {
              scale: 0,
              rotate: time % 2 === 0 ? 90 : -90,
              opacity: 0,
            },
            {
              scale: (3 - time + 4) / 7,
              rotate: 0,
              opacity: 1,
              duration: 0.5,
              ease: 'back.out(1.7)',
            },
            0
          )
          .fromTo(
            textRef.current,
            {
              scale: 0,
              rotate: time % 2 === 0 ? 15 : -15,
              opacity: 0,
            },
            {
              rotate: time % 2 === 0 ? -5 : 5,
              scale: 1,
              duration: 0.5,
              opacity: 1,
              ease: 'back.out(1.7)',
            },
            0.1
          )
      }
    })
    return () => ctx.revert()
  }, [nodeRef, backgroundRef, textRef, gsap, time])

  return (
    isVisible && (
      <div className='RunnerUICountdown fixed z-80 top-0 left-0 w-screen min-h-dvh min-w-full md:h-screen container content-center overflow-hidden pointer-events-none'>
        <SwitchTransition>
          <Transition
            key={time}
            appear
            nodeRef={nodeRef}
            onEnter={onEnter}
            timeout={{
              enter: 600,
              exit: 0,
            }}
          >
            <div
              ref={nodeRef}
              className='col-start-1 col-span-10 lg:col-start-3 lg:col-span-8 row-start-1 row-span-1 flex items-center justify-center'
            >
              {time > 0 && (
                <>
                  <UIIcon
                    ref={backgroundRef}
                    className={`absolute Countdown__Background top-0 left-0 w-full h-full ${colors[time % colors.length]}`}
                    icon={backgrounds[time % backgrounds.length] as UIIconType}
                  />
                  <div ref={textRef} className='relative font-heading font-semibold text-tertiary text-[14.86vw]'>
                    {time}
                  </div>
                  <UIParticles exclude={{ colors: excludedColors[time % excludedColors.length] }} max={5} />
                </>
              )}
            </div>
          </Transition>
        </SwitchTransition>
      </div>
    )
  )
}
